import { useEffect, useMemo, useRef } from "react";

/**
 * Keeps the current zoom across data loads.
 *
 * The Gantt rebuilds its state from its props whenever the task list changes,
 * which would snap the view back to the starting scale every time infinite
 * scroll pages in more services. Feeding the live zoom back into those props
 * leaves the user where they were.
 */
const useGanttZoomPersistence = ({ ganttApi, zoomConfig, tasks }) => {
	const liveZoom = useRef(null);

	useEffect(() => {
		if (!ganttApi) return undefined;

		return ganttApi.on("zoom-scale", () => {
			const state = ganttApi.getState();
			if (state?.zoom?.level === undefined) return;

			liveZoom.current = {
				level: state.zoom.level,
				cellWidth: state.cellWidth,
			};
		});
	}, [ganttApi]);

	// Recalculated only when the data changes, which is exactly when the Gantt
	// is about to rebuild itself
	return useMemo(() => {
		if (!zoomConfig) return { zoom: null, cellWidth: undefined };

		const live = liveZoom.current;
		if (!live) return { zoom: zoomConfig, cellWidth: undefined };

		return {
			zoom: { ...zoomConfig, level: live.level },
			cellWidth: live.cellWidth,
		};
	}, [zoomConfig, tasks]);
};

export default useGanttZoomPersistence;
